import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
  Segmented,
  Select,
  Space,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Empty from '@/components/QuietEmpty';
import {
  CalendarEventForm,
  type CalendarEventFormValues,
  calendarEventFormDefaultValues,
  createCalendarRelationFormValue,
  DEFAULT_SCHEDULE_EVENT_TYPE,
  normalizeCalendarRelationPayload,
  toCalendarRelationFormValues,
} from '@/modules/zy-crm/components/calendar-event-form';
import {
  MemberMultiPicker,
  MemberPicker,
} from '@/modules/zy-crm/components/member-picker';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  createCalendarEvent,
  queryCalendarEventsByBiz,
  updateCalendarEvent,
} from '@/services/bff/calendar.api';
import { queryDictionaryItemOptions } from '@/services/bff/dictionary-management.api';
import { createTask, queryTasks } from '@/services/bff/task.api';
import type {
  CalendarBizType,
  CalendarEventItem,
} from '@/services/bff/types/calendar';
import type { TaskItem } from '@/services/bff/types/task';
import { getCurrentMemberOption } from '@/utils/currentMember';
import { ActivityPanelFollowUps } from './follow-ups';
import styles from './style.module.less';
import type {
  ActivityPanelActivityItem,
  ActivityPanelData,
  ActivityPanelProps,
  ActivityTabItem,
  ActivityTabKey,
} from './types';

const { Paragraph, Text } = Typography;
const SCHEDULE_TYPE_DICTIONARY_CODE = 'schedule-types';
const MemberPickerField = MemberPicker as unknown as React.FC<any>;
const MemberMultiPickerField = MemberMultiPicker as unknown as React.FC<any>;

const activityIconMap: Record<string, ReactNode> = {
  calendarEvent: <CalendarOutlined />,
  task: <ClockCircleOutlined />,
};

const taskStatusLabelMap: Record<string, string> = {
  pending: '待处理',
  completed: '已完成',
  cancelled: '已取消',
};

const taskPriorityLabelMap: Record<string, string> = {
  high: '高优先级',
  medium: '中优先级',
  low: '低优先级',
};

const taskStatusOptions = [
  { value: 'pending', label: '待处理' },
  { value: 'completed', label: '已完成' },
  { value: 'cancelled', label: '已取消' },
];

const taskPriorityOptions = [
  { value: 'high', label: '高优先级' },
  { value: 'medium', label: '中优先级' },
  { value: 'low', label: '低优先级' },
];

type ActivityPanelDataState = ActivityPanelData;

type ActivityScheduleEditorState = {
  mode: 'create' | 'edit';
  item?: CalendarEventItem;
};

type TaskCreateFormValues = {
  title?: string;
  description?: string;
  priority?: string;
  status?: string;
  ownerUserUuid?: string;
  dueTime?: Dayjs;
};

function EmptyBlock({ description }: { description: string }) {
  return (
    <div className={styles.emptyBlock}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
    </div>
  );
}

function formatActivityTime(value?: string | null) {
  return value ? formatCrmDate(value) : '-';
}

function normalizeText(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeOptionalText(value: unknown) {
  return normalizeText(value) || undefined;
}

function normalizeLabelMap(
  list: Array<{ value?: string; label?: string }>,
): Record<string, string> {
  return Object.fromEntries(
    list
      .map(
        (item) =>
          [normalizeText(item.value), normalizeText(item.label)] as const,
      )
      .filter(([value, label]) => Boolean(value) && Boolean(label)),
  );
}

function resolveMappedText(
  map: Record<string, string>,
  value?: string | null,
  emptyText = '-',
) {
  const raw = normalizeText(value);
  if (!raw) {
    return emptyText;
  }
  return map[raw] || raw;
}

function resolveMemberText(
  memberNameMap: Record<string, string>,
  value?: string | null,
) {
  return resolveMappedText(memberNameMap, value);
}

function resolveParticipantText(
  memberNameMap: Record<string, string>,
  values?: string[] | null,
) {
  const list = Array.isArray(values) ? values : [];
  if (!list.length) {
    return '-';
  }
  return list
    .map((value) => resolveMemberText(memberNameMap, value))
    .join('、');
}

function normalizePage<T>(resp: unknown): { list: T[]; total: number } {
  if (!resp || typeof resp !== 'object') {
    return { list: [], total: 0 };
  }
  const root = resp as Record<string, unknown>;
  const candidates = [root, root.data, root.result, root.payload];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue;
    const obj = candidate as Record<string, unknown>;
    if (!Array.isArray(obj.list) && !Array.isArray(obj.items)) continue;
    const list = (obj.list || obj.items || []) as T[];
    return {
      list,
      total: Number(obj.total ?? obj.count ?? list.length) || 0,
    };
  }
  return { list: [], total: 0 };
}

function toActivityList(
  schedules: CalendarEventItem[],
  tasks: TaskItem[],
): ActivityPanelActivityItem[] {
  const scheduleItems = schedules.map((item) => ({
    key: `calendar-${item.uuid}`,
    type: 'calendarEvent',
    title: item.title || '未命名日程',
    description: item.description,
    time: item.start,
    dueTime: item.end,
    sourceResource: 'calendarEvent',
    sourceUuid: item.uuid,
  }));

  const taskItems = tasks.map((item) => ({
    key: `task-${item.uuid}`,
    type: 'task',
    title: item.title,
    description: item.description,
    time: item.updatedAt || item.createdAt || item.dueTime,
    dueTime: item.dueTime,
    sourceResource: 'task',
    sourceUuid: item.uuid,
    status: item.status,
  }));

  return [...scheduleItems, ...taskItems];
}

function activityIdentity(item: ActivityPanelActivityItem) {
  const sourceResource = normalizeText(item.sourceResource);
  const sourceUuid = normalizeText(item.sourceUuid);
  if (sourceResource && sourceUuid) {
    return `${sourceResource}:${sourceUuid}`;
  }
  return `key:${item.key}`;
}

function activityTimestamp(item: ActivityPanelActivityItem) {
  const value = new Date(item.time || item.dueTime || 0).getTime();
  return Number.isFinite(value) ? value : 0;
}

function mergeActivityLists(
  provided: ActivityPanelActivityItem[],
  loaded: ActivityPanelActivityItem[],
) {
  const activities = new Map<string, ActivityPanelActivityItem>();
  for (const item of [...provided, ...loaded]) {
    const identity = activityIdentity(item);
    if (!activities.has(identity)) {
      activities.set(identity, item);
    }
  }
  return Array.from(activities.values()).sort(
    (left, right) => activityTimestamp(right) - activityTimestamp(left),
  );
}

function isFollowUpActivity(item: ActivityPanelActivityItem) {
  const source = normalizeText(item.sourceResource).toLowerCase();
  const type = normalizeText(item.type).toLowerCase();
  return source.includes('follow') || type.includes('follow');
}

function activitySourceLabel(item: ActivityPanelActivityItem) {
  if (isFollowUpActivity(item)) return '跟进记录';
  if (item.sourceResource === 'calendarEvent') return '日程';
  if (item.sourceResource === 'task') return '任务';
  return '活动';
}

function renderActivities(items: ActivityPanelActivityItem[]) {
  if (!items.length) {
    return <EmptyBlock description="" />;
  }
  return (
    <Timeline
      className={styles.timeline}
      items={items.map((item) => ({
        icon: activityIconMap[item.sourceResource] || <ClockCircleOutlined />,
        content: (
          <div className={styles.timelineItem}>
            <div className={styles.recordTitleRow}>
              <span className={styles.recordTitle}>
                {item.title || '未命名活动'}
              </span>
              <Text type="secondary">
                {formatCrmDate(item.time || item.dueTime)}
              </Text>
            </div>
            {item.description ? (
              <Paragraph className={styles.recordDescription}>
                {item.description}
              </Paragraph>
            ) : null}
            <Space wrap size={[8, 8]}>
              <Tag>{activitySourceLabel(item)}</Tag>
            </Space>
          </div>
        ),
      }))}
    />
  );
}

function renderSchedules(
  schedules: CalendarEventItem[],
  onEdit: (item: CalendarEventItem) => void,
  scheduleTypeLabelMap: Record<string, string>,
  memberNameMap: Record<string, string>,
) {
  if (!schedules.length) {
    return <EmptyBlock description="" />;
  }
  const sortedSchedules = [...schedules].sort((a, b) => {
    const aTime = new Date(a.start || 0).getTime();
    const bTime = new Date(b.start || 0).getTime();
    return bTime - aTime;
  });
  return (
    <div className={styles.scheduleList}>
      {sortedSchedules.map((item) => (
        <div key={item.uuid} className={styles.scheduleCard}>
          <div className={styles.scheduleHeader}>
            <div className={styles.scheduleTitleArea}>
              <div className={styles.scheduleTitleRow}>
                <div className={styles.scheduleTitle}>
                  {item.title || '未命名日程'}
                </div>
                <Tag
                  variant="filled"
                  color="blue"
                  className={styles.scheduleTypeTag}
                >
                  {resolveMappedText(scheduleTypeLabelMap, item.eventType)}
                </Tag>
              </div>
              <div className={styles.scheduleMeta}>
                <span>{formatActivityTime(item.start)}</span>
                {item.end ? (
                  <span>至 {formatActivityTime(item.end)}</span>
                ) : null}
              </div>
            </div>
            <Button type="link" size="small" onClick={() => onEdit(item)}>
              编辑
            </Button>
          </div>
          {item.description ? (
            <Paragraph className={styles.scheduleDescription}>
              {item.description}
            </Paragraph>
          ) : null}
          <div className={styles.scheduleInfoRow}>
            <span className={styles.scheduleInfoItem}>
              <span className={styles.scheduleInfoLabel}>负责人</span>
              <span className={styles.scheduleInfoValue}>
                {resolveMemberText(memberNameMap, item.ownerMemberUuid)}
              </span>
            </span>
            <span className={styles.scheduleInfoItem}>
              <span className={styles.scheduleInfoLabel}>参与人</span>
              <span className={styles.scheduleInfoValue}>
                {resolveParticipantText(
                  memberNameMap,
                  item.participantMemberUuids,
                )}
              </span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function renderTasks(tasks: TaskItem[]) {
  if (!tasks.length) {
    return <EmptyBlock description="" />;
  }
  return (
    <div className={styles.taskGrid}>
      {tasks.map((item) => (
        <Card key={item.uuid} className={styles.taskCard} size="small">
          <div className={styles.taskCardBody}>
            <div className={styles.taskCardHeader}>
              <span className={styles.taskTitle}>{item.title}</span>
              <Text type="secondary" className={styles.taskCardTime}>
                {formatCrmDate(item.dueTime || item.updatedAt)}
              </Text>
            </div>
            <Space wrap size={[8, 8]} className={styles.taskCardMeta}>
              <Tag
                color={
                  item.status === 'completed'
                    ? 'green'
                    : item.status === 'cancelled'
                      ? 'default'
                      : 'blue'
                }
              >
                {taskStatusLabelMap[String(item.status || '')] || '待处理'}
              </Tag>
              <Tag
                color={
                  item.priority === 'high'
                    ? 'red'
                    : item.priority === 'low'
                      ? 'default'
                      : 'orange'
                }
              >
                {taskPriorityLabelMap[String(item.priority || 'medium')] ||
                  '中优先级'}
              </Tag>
            </Space>
            {item.description ? (
              <Paragraph className={styles.taskDescription}>
                {item.description}
              </Paragraph>
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

export function ActivityPanel(props: ActivityPanelProps) {
  const [activeTab, setActiveTab] = useState<ActivityTabKey>(
    props.defaultTab || 'all',
  );
  const { ensureMembersLoaded, memberNameMap } = useMemberDirectory();
  const [scheduleTypeLabelMap, setScheduleTypeLabelMap] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<ActivityPanelDataState>({
    activities: mergeActivityLists(props.activities || [], []),
    schedules: [],
    tasks: { list: [], total: 0 },
  });
  const followUpCountLoaded = useRef(false);
  const [followUpCount, setFollowUpCount] = useState<number | undefined>(() => {
    const count = (props.activities || []).filter(isFollowUpActivity).length;
    return count || undefined;
  });
  const [scheduleEditor, setScheduleEditor] =
    useState<ActivityScheduleEditorState | null>(null);
  const [scheduleForm] = Form.useForm<CalendarEventFormValues>();
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm] = Form.useForm<TaskCreateFormValues>();
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    void queryDictionaryItemOptions(SCHEDULE_TYPE_DICTIONARY_CODE)
      .then((list) => {
        if (!active) return;
        setScheduleTypeLabelMap(
          normalizeLabelMap(
            (list || []).map((item) => ({
              value: item.value,
              label: item.label,
            })),
          ),
        );
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    void ensureMembersLoaded().catch(() => undefined);
  }, [ensureMembersLoaded]);

  const loadData = useCallback(async () => {
    if (!props.bizType || !props.bizUuid) {
      setData({
        activities: mergeActivityLists(props.activities || [], []),
        schedules: [],
        tasks: { list: [], total: 0 },
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [scheduleResp, taskResp] = await Promise.all([
        queryCalendarEventsByBiz(
          props.bizType as CalendarBizType,
          props.bizUuid,
        ),
        queryTasks({
          bizType: props.bizType,
          bizUuid: props.bizUuid,
          page: 1,
          pageSize: 50,
        }),
      ]);
      const tasks = normalizePage<TaskItem>(taskResp);
      const schedules = Array.isArray(scheduleResp) ? scheduleResp : [];
      setData({
        activities: mergeActivityLists(
          props.activities || [],
          toActivityList(schedules, tasks.list),
        ),
        schedules,
        tasks,
      });
      props.onDataLoaded?.();
    } catch (e) {
      setError(e instanceof Error ? e : new Error('活动数据加载失败'));
      setData((current) => ({
        ...current,
        activities: mergeActivityLists(props.activities || [], []),
      }));
    } finally {
      setLoading(false);
    }
  }, [props.activities, props.bizType, props.bizUuid, props.onDataLoaded]);

  useEffect(() => {
    followUpCountLoaded.current = false;
  }, [props.bizType, props.bizUuid]);

  useEffect(() => {
    const count = (props.activities || []).filter(isFollowUpActivity).length;
    if (!followUpCountLoaded.current) setFollowUpCount(count || undefined);
  }, [props.activities]);

  useEffect(() => {
    setActiveTab(props.defaultTab || 'all');
  }, [props.defaultTab]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    props.onBusyChange?.(loading);
  }, [loading, props.onBusyChange]);

  const openCreateSchedule = useCallback(() => {
    scheduleForm.resetFields();
    scheduleForm.setFieldsValue(
      calendarEventFormDefaultValues({
        title: '',
        eventType: DEFAULT_SCHEDULE_EVENT_TYPE,
        allDay: false,
        startTime: dayjs().minute(0).second(0),
        endTime: dayjs().add(1, 'hour').minute(0).second(0),
        relations: [
          createCalendarRelationFormValue(
            props.bizType as CalendarBizType,
            props.bizUuid,
          ),
        ],
        ownerMemberUuid: getCurrentMemberOption()?.value || undefined,
        participantMemberUuids: [],
      }),
    );
    setScheduleEditor({ mode: 'create' });
  }, [props.bizType, props.bizUuid, scheduleForm]);

  const openEditSchedule = useCallback(
    (item: CalendarEventItem) => {
      scheduleForm.resetFields();
      scheduleForm.setFieldsValue(
        calendarEventFormDefaultValues({
          ...item,
          startTime: item.start ? dayjs(item.start) : undefined,
          endTime: item.end ? dayjs(item.end) : undefined,
          relations: item.relations
            ? toCalendarRelationFormValues(item.relations)
            : [
                createCalendarRelationFormValue(
                  props.bizType as CalendarBizType,
                  props.bizUuid,
                ),
              ],
        }),
      );
      setScheduleEditor({ mode: 'edit', item });
    },
    [props.bizType, props.bizUuid, scheduleForm],
  );

  const closeSchedule = useCallback(() => {
    setScheduleEditor(null);
    scheduleForm.resetFields();
  }, [scheduleForm]);

  const submitSchedule = useCallback(async () => {
    if (!scheduleEditor) return;
    const values = await scheduleForm.validateFields();
    setScheduleSubmitting(true);
    try {
      const payload = {
        title: normalizeOptionalText(values.title) || '',
        eventType:
          normalizeOptionalText(values.eventType) ||
          DEFAULT_SCHEDULE_EVENT_TYPE,
        startTime: values.startTime?.toISOString() || '',
        endTime: values.endTime?.toISOString(),
        allDay: Boolean(values.allDay),
        location: normalizeOptionalText(values.location),
        description: normalizeOptionalText(values.description),
        ownerMemberUuid: normalizeOptionalText(values.ownerMemberUuid),
        participantMemberUuids: values.participantMemberUuids || [],
        relations: normalizeCalendarRelationPayload(values.relations),
      };
      if (scheduleEditor.mode === 'edit' && scheduleEditor.item?.uuid) {
        await updateCalendarEvent(scheduleEditor.item.uuid, payload);
        message.success('日程已更新');
      } else {
        await createCalendarEvent(payload as any);
        message.success('日程已创建');
      }
      closeSchedule();
      await loadData();
    } finally {
      setScheduleSubmitting(false);
    }
  }, [closeSchedule, loadData, scheduleEditor, scheduleForm]);

  const openCreateTask = useCallback(() => {
    taskForm.resetFields();
    taskForm.setFieldsValue({
      title: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      ownerUserUuid: getCurrentMemberOption()?.value || undefined,
      dueTime: dayjs().add(1, 'day'),
    });
    setTaskOpen(true);
  }, [taskForm]);

  const closeTask = useCallback(() => {
    setTaskOpen(false);
    taskForm.resetFields();
  }, [taskForm]);

  const submitTask = useCallback(async () => {
    const values = await taskForm.validateFields();
    setTaskSubmitting(true);
    try {
      await createTask({
        bizType: props.bizType,
        bizUuid: props.bizUuid,
        title: normalizeOptionalText(values.title) || '',
        description: normalizeOptionalText(values.description),
        priority: normalizeOptionalText(values.priority),
        status: normalizeOptionalText(values.status),
        ownerUserUuid: normalizeOptionalText(values.ownerUserUuid),
        dueTime: values.dueTime ? values.dueTime.toISOString() : undefined,
      });
      message.success('任务已创建');
      closeTask();
      await loadData();
    } finally {
      setTaskSubmitting(false);
    }
  }, [closeTask, loadData, props.bizType, props.bizUuid, taskForm]);

  const tabItems = useMemo<ActivityTabItem[]>(
    () => [
      { key: 'all', label: '全部', count: data.activities.length },
      { key: 'schedule', label: '日程', count: data.schedules.length },
      { key: 'followUps', label: '跟进记录', count: followUpCount },
      { key: 'tasks', label: '任务', count: data.tasks.total },
    ],
    [
      data.activities.length,
      data.schedules.length,
      data.tasks.total,
      followUpCount,
    ],
  );

  const toolbarAction =
    activeTab === 'schedule'
      ? {
          label: '新增日程',
          onClick: openCreateSchedule,
        }
      : activeTab === 'tasks'
        ? {
            label: '新增任务',
            onClick: openCreateTask,
          }
        : null;

  let content: ReactNode;
  switch (activeTab) {
    case 'schedule':
      content = renderSchedules(
        data.schedules,
        openEditSchedule,
        scheduleTypeLabelMap,
        memberNameMap,
      );
      break;
    case 'followUps':
      content = (
        <ActivityPanelFollowUps
          bizType={props.bizType}
          bizUuid={props.bizUuid}
          bizName={props.bizName}
          onRecordSaved={props.onDataLoaded}
          onTotalChange={(total) => {
            followUpCountLoaded.current = true;
            setFollowUpCount(total);
          }}
        />
      );
      break;
    case 'tasks':
      content = renderTasks(data.tasks.list);
      break;
    default:
      content = renderActivities(data.activities);
  }

  return (
    <div className={styles.panel}>
      {error ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space orientation="vertical" size={4}>
            <Text type="danger">活动数据加载失败</Text>
            <Text type="secondary">{error.message}</Text>
          </Space>
        </Card>
      ) : null}

      <div className={styles.tabBar}>
        <Segmented
          value={activeTab}
          options={tabItems.map((item) => ({
            label:
              item.count === undefined
                ? item.label
                : `${item.label} ${item.count ?? 0}`,
            value: item.key,
          }))}
          onChange={(value) => setActiveTab(value as ActivityTabKey)}
        />
      </div>

      <div className={styles.actionBar}>
        {toolbarAction ? (
          <Button type="link" size="small" onClick={toolbarAction.onClick}>
            {toolbarAction.label}
          </Button>
        ) : null}
      </div>

      {content}

      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={Boolean(scheduleEditor)}
        title={scheduleEditor?.mode === 'edit' ? '编辑日程' : '新增日程'}
        onCancel={closeSchedule}
        onOk={() => void submitSchedule()}
        confirmLoading={scheduleSubmitting}
        destroyOnHidden
        forceRender
      >
        <CalendarEventForm
          form={scheduleForm}
          showOwnerMemberField
          showParticipantMemberField
          ownerMemberField={
            <MemberPickerField
              adapter={orgDirectoryAdapter}
              placeholder="请选择负责人"
            />
          }
          participantMemberField={
            <MemberMultiPickerField
              adapter={orgDirectoryAdapter}
              placeholder="请选择参与人"
            />
          }
        />
      </Modal>

      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={taskOpen}
        title="新增任务"
        onCancel={closeTask}
        onOk={() => void submitTask()}
        confirmLoading={taskSubmitting}
        destroyOnHidden
        forceRender
      >
        <Form form={taskForm} layout="vertical">
          <Form.Item
            name="title"
            label="任务标题"
            rules={[{ required: true, message: '请输入任务标题' }]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="description" label="任务说明">
            <Input.TextArea rows={4} maxLength={500} />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <Select options={taskPriorityOptions} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={taskStatusOptions} />
          </Form.Item>
          <Form.Item name="ownerUserUuid" label="负责人">
            <MemberPickerField
              adapter={orgDirectoryAdapter}
              placeholder="请选择负责人"
            />
          </Form.Item>
          <Form.Item name="dueTime" label="截止时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export * from './types';
